import { useCallback, useEffect, useRef, useState } from 'react'

const MIN_WIDTH = 280
const MAX_WIDTH = 900

/**
 * Hook pour rendre une sidebar redimensionnable par drag.
 * La largeur est persistée dans localStorage.
 *
 * @param storageKey  Clé localStorage pour persister la largeur
 * @param defaultWidth  Largeur par défaut en px
 * @param edge  'right' = handle sur le bord droit (sidebar gauche, ex: menu nav)
 *              'left'  = handle sur le bord gauche (sidebar droite, ex: panneau détail)
 */
export function useSidebarResize(storageKey: string, defaultWidth = 384, edge: 'left' | 'right' = 'left') {
  const [width, setWidth] = useState<number>(() => {
    const stored = localStorage.getItem(storageKey)
    return stored ? parseInt(stored, 10) : defaultWidth
  })
  const [isResizing, setIsResizing] = useState(false)
  const startX = useRef(0)
  const startWidth = useRef(0)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      startX.current = e.clientX
      startWidth.current = width
      setIsResizing(true)
      e.preventDefault()
    },
    [width],
  )

  useEffect(() => {
    if (!isResizing) return

    const onMouseMove = (e: MouseEvent) => {
      // edge='right' (sidebar gauche) : drag droite = agrandir → delta = currentX - startX
      // edge='left'  (sidebar droite) : drag gauche = agrandir → delta = startX - currentX
      const delta = edge === 'right'
        ? e.clientX - startX.current
        : startX.current - e.clientX
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta))
      setWidth(newWidth)
    }

    const onMouseUp = () => {
      setIsResizing(false)
      setWidth(w => {
        localStorage.setItem(storageKey, String(w))
        return w
      })
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    // Curseur col-resize pendant le drag
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, storageKey, edge])

  return { width, handleMouseDown, isResizing }
}
